import { useRef, useState } from 'react';

import { curatorErrorCopy, useCuratorPrefillImport } from '../../api/curator';
import {
  PREFILL_IMPORT_ACCEPT,
  importReportSections,
  importSummaryLine,
  prefillImportFileError,
  type CuratorPrefillImportReport,
} from '../../api/curator-model';
import { Button, Modal } from '../../components/ui';
import { ImportSection } from './prefill-import-table';
import './cabinet.css';

/**
 * «Загрузить из файла» (contract #26.7, ADDITIVE v1.8.9 — T.Z46; the button was
 * «Загрузчить Excel» until T.Z63 renamed it, and the drawer became THIS wide modal
 * the same day): an .xlsx/.csv of the SAME five columns the form types by hand (ФИО,
 * Номер заявления, УНТИ ID, Регион, Программа/Курс) onto the venue this table is
 * about, with the api's per-row report as the answer.
 *
 * THE RULES ARE THE SERVER'S, the modal only narrates them: a row that is ALREADY
 * FOUND is skipped and reported («уже найденные» — the natural key first, then the
 * same «номер заявления», the same УНТИ id, or the same folded ФИО on this venue, so
 * a re-uploaded file is a no-op by construction), a bad ФИО or an unrecognised
 * programme is reported (the bad ФИО is refused outright since T.Z63 — no form can
 * edit one; the unresolved programme imports flagged), and only then does the rest of
 * the file land. A refusal of the WHOLE file (a foreign venue, the flag off, the
 * partner pool full) surfaces through `curatorErrorCopy`, `meta.limit` included.
 *
 * THE MODAL, not the drawer (T.Z63): the owner's file carries hundreds of rows, so
 * the answer opens WIDE and groups into three COLLAPSED sections — «Уже найденные»,
 * «Загруженные», «С ошибками» — each folded by default behind a count badge, so a
 * 500-line report reads as three numbers first and a table only on demand. Every
 * section's table carries the row's own cells (v1.9.10; T.Z74 set the columns to the
 * file's own five — ФИО, Номер заявления, УНТИ ID, Регион, Программа/Курс — dropped
 * «Строка» and made each cell a real table cell again): the api echoes the uploader
 * their own file's content, so an operator fixes a refused line by reading it, not by
 * recounting sheet rows. The table itself lives in `prefill-import-table.tsx`.
 *
 * The pre-checks here are comfort, not authority: the extension and the size are
 * things the api would refuse anyway, so the modal says so before the wait.
 */
export function PrefillImportModal({
  venueId,
  venueName,
  onClose,
  onImported,
}: {
  /** The venue every row lands on (#26.3's rule: ONE venue, in scope, flag on). */
  venueId: number;
  venueName: string;
  onClose: () => void;
  /** Fired once the file is in — the block shows its notice and refetches. */
  onImported: (report: CuratorPrefillImportReport) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [report, setReport] = useState<CuratorPrefillImportReport | null>(null);
  const importMutation = useCuratorPrefillImport();
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = importMutation.isPending;

  const pick = (next: File | null) => {
    setLocalError(null);
    setFile(next);
    // A new file retires the previous answer: the modal narrates ONE upload.
    setReport(null);
    if (next !== null) {
      const problem = prefillImportFileError(next);
      if (problem !== null) {
        setLocalError(problem);
        setFile(null);
        if (inputRef.current !== null) inputRef.current.value = '';
      }
    }
  };

  const send = () => {
    if (file === null) {
      setLocalError('Выберите файл .xlsx или .csv.');
      return;
    }
    importMutation.mutate(
      { file, venueId },
      {
        onSuccess: (next) => {
          setReport(next);
          onImported(next);
        },
        onError: (error) => setLocalError(copyOf(error)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title="Загрузить из файла"
      description={`${venueName} · столбцы: ФИО, Номер заявления, УНТИ ID, Регион, Программа/Курс`}
    >
      <div className="prefill-import" data-testid="prefill-import">
        <p className="cabinet-note">
          Файл загружается на выбранную площадку. Уже найденные строки пропускаются, строка с нераспознанной программой
          загрузится с пометкой, строка с плохим ФИО или номером — не загрузится.
        </p>

        <input
          ref={inputRef}
          id="prefill-import-file"
          className="input"
          type="file"
          accept={PREFILL_IMPORT_ACCEPT}
          data-testid="prefill-import-file"
          onChange={(event) => pick(event.target.files?.[0] ?? null)}
        />

        {file !== null ? (
          <p className="cabinet-note" data-testid="prefill-import-file-name">
            {file.name}
          </p>
        ) : null}

        {localError !== null ? (
          <p className="field__error" role="alert" data-testid="prefill-import-error">
            {localError}
          </p>
        ) : null}

        <div className="cabinet-duplicate__actions">
          <Button
            variant="primary"
            size="sm"
            data-testid="prefill-import-submit"
            disabled={file === null}
            loading={busy}
            onClick={send}
          >
            Загрузить
          </Button>
          <Button variant="ghost" size="sm" data-testid="prefill-import-close" onClick={onClose}>
            Закрыть
          </Button>
        </div>

        {report !== null ? <ImportReport report={report} /> : null}
      </div>
    </Modal>
  );
}

/**
 * The per-row report — the answer IS the feedback. The totals line first (rowsTotal =
 * найденные + загруженные + ошибки), then the three collapsed sections, each behind a
 * count badge; a section opens into one line per row with the file's own five cells,
 * the outcome and the reason.
 */
function ImportReport({ report }: { report: CuratorPrefillImportReport }) {
  return (
    <section className="prefill-import__report" data-testid="prefill-import-report">
      <p className="cabinet-notice" role="status" data-testid="prefill-import-summary">
        {importSummaryLine(report)}
      </p>
      <div className="prefill-import__sections">
        {importReportSections(report).map((section) => (
          <ImportSection key={section.key} section={section} />
        ))}
      </div>
    </section>
  );
}

/** The api's refusal, verbatim in wording (its `meta.limit` included). */
function copyOf(error: unknown): string {
  return curatorErrorCopy(error);
}
